/**
 * ProductCardSkeleton Component - Story 7.6: Add Loading and Error States
 * Enhanced loading skeleton that matches ProductCard layout with smooth animations
 *
 * Features:
 * - Smooth pulse animation for visual feedback
 * - Matches exact ProductCard layout
 * - Proper ARIA attributes for accessibility
 * - Responsive design matching ProductCard
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface ProductCardSkeletonProps {
  /** Optional CSS class name */
  className?: string;
}

/**
 * ProductCardSkeleton Component
 * Loading skeleton that perfectly matches the ProductCard layout
 * Uses smooth pulse animation for visual loading feedback
 *
 * Layout matches ProductCard:
 * - Square image area with category badge
 * - Product name (2 lines)
 * - Price and unit
 * - Location with icon
 *
 * @component
 * @example
 * // Single skeleton
 * <ProductCardSkeleton />
 *
 * @example
 * // Grid of skeletons
 * {Array.from({ length: 12 }).map((_, i) => (
 *   <ProductCardSkeleton key={i} />
 * ))}
 *
 * @example
 * // With custom styling
 * <ProductCardSkeleton className="opacity-75" />
 */
export const ProductCardSkeleton: React.FC<ProductCardSkeletonProps> = ({ className }) => {
  return (
    <Card
      className={cn('overflow-hidden', className)}
      aria-busy="true"
      aria-live="polite"
    >
      {/* Image Section - Square aspect ratio matching ProductCard */}
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <Skeleton className="h-full w-full animate-pulse" />

        {/* Category Badge Skeleton - positioned absolute like ProductCard */}
        <div className="absolute left-3 top-3">
          <Skeleton className="h-6 w-24 animate-pulse rounded-full" />
        </div>

        {/* Favorite Button Skeleton - positioned absolute like ProductCard */}
        <div className="absolute right-3 top-3">
          <Skeleton className="h-8 w-8 animate-pulse rounded-full" />
        </div>
      </div>

      {/* Content Section */}
      <CardContent className="p-4">
        {/* Product Name - Two lines of text */}
        <div className="mb-3 space-y-2">
          <Skeleton className="h-5 w-full animate-pulse" />
          <Skeleton className="h-5 w-4/5 animate-pulse" />
        </div>

        {/* Price Section - Price and unit */}
        <div className="mb-3 flex items-baseline gap-2">
          <Skeleton className="h-7 w-24 animate-pulse" />
          <Skeleton className="h-4 w-16 animate-pulse" />
        </div>

        {/* Location Section - Icon and location text */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 animate-pulse rounded-full flex-shrink-0" />
          <Skeleton className="h-4 w-32 animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCardSkeleton;
